/**
 * PLX Code Scanner Component
 * 
 * Uses device camera to scan custom 7x7 binary PLX tags
 * Decodes the tag and extracts the asset ID
 * Uses OpenCV.js for robust contour detection and perspective warping
 */

import React, { useRef, useEffect, useState } from 'react';
import { Camera, X, AlertCircle } from 'lucide-react';

// OpenCV.js loading state
let cvReady = false;
let cvLoading = false;

// Wait for OpenCV to load
function checkCv() {
  if (typeof cv !== 'undefined' && cv.Mat && cv.Mat.prototype) {
    // Check if OpenCV is fully initialized
    try {
      new cv.Mat(); // Try creating a Mat to verify it's ready
      cvReady = true;
      return true;
    } catch (e) {
      // Not fully initialized yet
      return false;
    }
  } else if (!cvLoading && typeof window !== 'undefined') {
    cvLoading = true;
    // Load OpenCV.js from CDN (using a reliable CDN)
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/opencv-js@1.2.1/opencv.js';
    script.async = true;
    script.onload = () => {
      // Wait for OpenCV to initialize
      if (typeof cv !== 'undefined') {
        if (cv.onRuntimeInitialized) {
          cv.onRuntimeInitialized = () => {
            cvReady = true;
            cvLoading = false;
          };
        } else {
          // If already initialized
          setTimeout(() => {
            if (typeof cv !== 'undefined' && cv.Mat) {
              cvReady = true;
              cvLoading = false;
            }
          }, 100);
        }
      }
    };
    script.onerror = () => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load OpenCV.js, trying alternative CDN...');
      }
      // Try alternative CDN
      const script2 = document.createElement('script');
      script2.src = 'https://docs.opencv.org/4.x/opencv.js';
      script2.async = true;
      script2.onload = () => {
        setTimeout(() => {
          if (typeof cv !== 'undefined' && cv.Mat) {
            cvReady = true;
            cvLoading = false;
          }
        }, 500);
      };
      script2.onerror = () => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to load OpenCV.js from all sources');
        }
        cvLoading = false;
      };
      document.head.appendChild(script2);
    };
    document.head.appendChild(script);
    return false;
  }
  return false;
}

// Initialize OpenCV check
if (typeof window !== 'undefined') {
  checkCv();
  // Also check periodically in case it loads asynchronously
  const checkInterval = setInterval(() => {
    if (checkCv() && cvReady) {
      clearInterval(checkInterval);
    }
  }, 200);
}

const GRID_SIZE = 7;
const THRESH_VAL = 100; // Match working version

/**
 * Decode a 7x7 PLX tag from video element using OpenCV.js
 * @param {HTMLVideoElement} videoElement - Video element with current frame
 * @param {HTMLCanvasElement} canvasElement - Canvas for visualization
 * @returns {string|null} - Decoded asset ID or null if not found
 */
function scanFrameForPLX(videoElement, canvasElement) {
  if (!cvReady || !videoElement || videoElement.readyState !== videoElement.HAVE_ENOUGH_DATA) {
    return null;
  }

  // Check if video has valid dimensions
  if (!videoElement.videoWidth || !videoElement.videoHeight || 
      videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
    return null;
  }

  // Declare variables outside try block for cleanup
  let src, gray, binary, contours, hierarchy;
  
  try {
    // Use canvas-based approach for more reliable dimension handling
    // Create a temporary canvas to capture the video frame
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = videoElement.videoWidth;
    tempCanvas.height = videoElement.videoHeight;
    const tempCtx = tempCanvas.getContext('2d');
    tempCtx.drawImage(videoElement, 0, 0, tempCanvas.width, tempCanvas.height);
    
    // Get image data from canvas
    const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
    
    // Create OpenCV Mat from image data
    src = cv.matFromImageData(imageData);

    gray = new cv.Mat();
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);

    binary = new cv.Mat();
    cv.threshold(gray, binary, THRESH_VAL, 255, cv.THRESH_BINARY);

    // 2. Find Contours
    contours = new cv.MatVector();
    hierarchy = new cv.Mat();
    cv.findContours(binary, contours, hierarchy, cv.RETR_TREE, cv.CHAIN_APPROX_SIMPLE);

    let foundTag = null;

    for (let i = 0; i < contours.size(); ++i) {
      let cnt = contours.get(i);
      let area = cv.contourArea(cnt);
      
      // STRICT CHECK 1: Min Size - Ignore small specks (Ghosts)
      if (area < 4000) {
        cnt.delete();
        continue;
      }

      let peri = cv.arcLength(cnt, true);
      let approx = new cv.Mat();
      cv.approxPolyDP(cnt, approx, 0.04 * peri, true);

      // STRICT CHECK 2: Shape - Must be 4 corners and convex
      if (approx.rows === 4 && cv.isContourConvex(approx)) {
        
        // STRICT CHECK 3: Aspect Ratio (Squareness)
        // Real tags are 1:1. Shadows are usually long rectangles.
        let rect = cv.boundingRect(approx);
        let ratio = rect.width / rect.height;
        
        // Allow 20% deviation (0.8 to 1.2)
        if (ratio > 0.8 && ratio < 1.2) {

          let warped = warpTag(binary, approx);
          if (warped) {
            
            // STRICT CHECK 4: Border Check (Quiet Zone)
            // We check the top-left corner. It MUST be black (the tag border).
            // If it is white (> 128), it's likely a random wall/paper.
            let roi = warped.roi(new cv.Rect(5, 5, 10, 10));
            let meanVal = cv.mean(roi);
            roi.delete();

            // Python logic: if mean > 128 continue. 
            // So we only proceed if mean <= 128 (Dark)
            if (meanVal[0] <= 128) {
              
              let grid = readGrid(warped);
              let result = tryDecode(grid);
              
              if (result.valid) {
                foundTag = result.id; // Return just the ID, not "PLX-" prefix
                drawGreenBox(canvasElement, approx, videoElement);
                warped.delete();
                approx.delete();
                cnt.delete();
                break; 
              }
            }
            warped.delete();
          }
        }
      }
      approx.delete();
      cnt.delete();
    }

    // Cleanup Memory
    src.delete();
    gray.delete();
    binary.delete();
    contours.delete();
    hierarchy.delete();
    
    return foundTag;
  } catch (error) {
    console.error('OpenCV processing error:', error);
    // Try to cleanup on error
    try {
      if (src) src.delete();
      if (gray) gray.delete();
      if (binary) binary.delete();
      if (contours) contours.delete();
      if (hierarchy) hierarchy.delete();
    } catch (cleanupError) {
      // Ignore cleanup errors
    }
    return null;
  }
}

/**
 * Perspective Warp - Normalize tag to square
 */
function warpTag(binaryImage, approx) {
  if (!cvReady) return null;
  
  let pts = [];
  for(let i = 0; i < 4; i++) {
    pts.push({ x: approx.data32S[i*2], y: approx.data32S[i*2+1] });
  }

  // Sort Points: TL, TR, BR, BL
  pts.sort((a, b) => a.y - b.y);
  let top = pts.slice(0, 2).sort((a, b) => a.x - b.x);
  let bot = pts.slice(2, 4).sort((a, b) => a.x - b.x);
  let corners = [top[0], top[1], bot[1], bot[0]];

  let side = 300;
  let srcTri = cv.matFromArray(4, 1, cv.CV_32FC2, [
    corners[0].x, corners[0].y, corners[1].x, corners[1].y,
    corners[2].x, corners[2].y, corners[3].x, corners[3].y
  ]);
  let dstTri = cv.matFromArray(4, 1, cv.CV_32FC2, [0, 0, side-1, 0, side-1, side-1, 0, side-1]);
  
  let M = cv.getPerspectiveTransform(srcTri, dstTri);
  let warped = new cv.Mat();
  cv.warpPerspective(binaryImage, warped, M, new cv.Size(side, side));

  srcTri.delete();
  dstTri.delete();
  M.delete();
  return warped;
}

/**
 * Read Grid from warped image
 */
function readGrid(warpedImage) {
  if (!cvReady) return null;
  
  let grid = [];
  let side = 300;
  // Python Logic: Grid Size + 2 (Border)
  let cell = side / (GRID_SIZE + 2); 

  for(let row = 0; row < GRID_SIZE; row++) {
    let rowData = [];
    for(let col = 0; col < GRID_SIZE; col++) {
      let cx = Math.floor((col + 1) * cell + (cell / 2));
      let cy = Math.floor((row + 1) * cell + (cell / 2));
      
      // Standard: > 128 is White (1)
      let pixel = warpedImage.ucharPtr(cy, cx)[0];
      rowData.push(pixel > 128 ? 1 : 0);
    }
    grid.push(rowData);
  }
  return grid;
}

/**
 * Try to decode grid with all rotations and flips
 */
function tryDecode(grid) {
  // 1. Standard - Try all 4 rotations
  let g = grid;
  for(let r = 0; r < 4; r++) {
    let res = checkMath(g);
    if(res.valid) return { valid: true, id: res.id };
    g = rotateGrid(g);
  }
  
  // 2. Mirrored - Try all 4 rotations of flipped grid
  let m = flipGrid(grid);
  for(let r = 0; r < 4; r++) {
    let res = checkMath(m);
    if(res.valid) return { valid: true, id: res.id };
    m = rotateGrid(m);
  }
  
  return { valid: false };
}

/**
 * Rotate grid 90 degrees clockwise
 */
function rotateGrid(grid) {
  const N = grid.length;
  let newGrid = Array.from({length: N}, () => Array(N).fill(0));
  for(let r = 0; r < N; r++) {
    for(let c = 0; c < N; c++) {
      newGrid[c][N-1-r] = grid[r][c];
    }
  }
  return newGrid;
}

/**
 * Flip grid horizontally
 */
function flipGrid(grid) {
  return grid.map(row => [...row].reverse());
}

/**
 * Extract ID from grid (LSB first, row-major)
 */
function extractIdFromGrid(grid) {
  let payload = 0n;
  let bitIndex = 0n;
  for(let row = 0; row < GRID_SIZE; row++) {
    for(let col = 0; col < GRID_SIZE; col++) {
      if(grid[row][col] === 1) payload |= (1n << bitIndex);
      bitIndex++;
    }
  }
  return payload;
}

/**
 * Check math validation (anchors, safety checksum)
 */
function checkMath(grid) {
  let payload = extractIdFromGrid(grid);
  let readSafety = Number(payload & 0xFFn); 
  let readAnchors = Number((payload >> 45n) & 0xFn);
  
  // Check Anchors (15)
  if (readAnchors !== 15) return { valid: false };

  // Check ID
  let mask37 = (1n << 37n) - 1n;
  let readId = Number((payload >> 8n) & mask37);
  
  // Check Safety
  if ((readId % 255) === readSafety && readId > 0) {
    return { valid: true, id: readId };
  }
  return { valid: false };
}

/**
 * Draw green box around detected tag
 */
function drawGreenBox(canvas, approxPoly, videoElement) {
  if (!canvas || !approxPoly || !videoElement) return;
  
  const ctx = canvas.getContext('2d');
  if (canvas.width !== videoElement.videoWidth) {
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
  }
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  
  ctx.beginPath();
  ctx.lineWidth = 6;
  ctx.strokeStyle = "#00FF00"; 
  
  let pts = approxPoly.data32S; 
  ctx.moveTo(pts[0], pts[1]);
  ctx.lineTo(pts[2], pts[3]);
  ctx.lineTo(pts[4], pts[5]);
  ctx.lineTo(pts[6], pts[7]);
  ctx.closePath();
  ctx.stroke();
}

export default function PLXScanner({ onScan, onClose, isDark, embedded = false }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [stream, setStream] = useState(null);
  const [scanningStatus, setScanningStatus] = useState('Starting camera...');
  const scanIntervalRef = useRef(null);

  // Start camera stream
  useEffect(() => {
    const startCamera = async () => {
      try {
        setScanningStatus('Requesting camera access...');
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment', // Use rear camera on mobile devices
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        });
        
        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
        setScanningStatus('Scanning for PLX code...');
        setIsScanning(true);
      } catch (err) {
        console.error('Camera access error:', err);
        if (err.name === 'NotAllowedError') {
          setError('Camera access denied. Please allow camera permissions and try again.');
        } else if (err.name === 'NotFoundError') {
          setError('No camera found on this device.');
        } else {
          setError('Unable to access camera. Please ensure camera permissions are granted.');
        }
      }
    };

    startCamera();

    return () => {
      // Cleanup: stop camera stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
      }
    };
  }, []);

  // Process video frames to detect PLX tags
  useEffect(() => {
    if (!isScanning || !videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    const scanFrame = () => {
      // Check if OpenCV is ready
      if (!cvReady) {
        // Keep checking for OpenCV
        checkCv();
        return;
      }

      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        // Use OpenCV-based scanning with strict validation
        const decodedId = scanFrameForPLX(video, canvas);

        if (decodedId) {
          console.log('✓ PLX tag detected:', decodedId);
          setScanningStatus('PLX code detected!');
          
          // Stop scanning
          setIsScanning(false);
          if (scanIntervalRef.current) {
            clearInterval(scanIntervalRef.current);
          }
          
          // Stop camera
          if (stream) {
            stream.getTracks().forEach(track => track.stop());
          }
          
          // Brief delay to show success message
          setTimeout(() => {
            // Notify parent component
            onScan(String(decodedId));
          }, 300);
        }
      }
    };

    // Scan frames at 10 FPS
    scanIntervalRef.current = setInterval(scanFrame, 100);

    return () => {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
      }
    };
  }, [isScanning, stream, onScan]);

  const handleClose = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }
    onClose();
  };

  // Camera + overlay content (shared between embedded and standalone modes)
  const cameraContent = (
    <div className="p-4">
      {error ? (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <AlertCircle size={48} className="text-red-400 mb-4" />
          <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{error}</p>
          <button
            onClick={handleClose}
            className="mt-4 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      ) : (
        <div className="relative">
          {/* Video preview */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full rounded-lg bg-black"
            style={{ maxHeight: embedded ? '45vh' : '60vh' }}
          />
          
          {/* Canvas for OpenCV processing and visualization */}
          <canvas 
            ref={canvasRef} 
            style={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              width: '100%', 
              height: '100%',
              pointerEvents: 'none',
              zIndex: 10
            }} 
          />
          
          {/* Scanning overlay */}
          {isScanning && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="border-2 border-blue-500 rounded-lg" style={{ width: '60%', height: '60%' }}>
                <div className="absolute inset-0 border-2 border-blue-400 animate-pulse rounded-lg"></div>
              </div>
            </div>
          )}
          
          {/* Instructions & Status */}
          <div className="absolute bottom-4 left-0 right-0 flex justify-center">
            <div className={`px-4 py-2 rounded-lg backdrop-blur-md ${isDark ? 'bg-slate-900/80 text-slate-200' : 'bg-white/80 text-slate-800'}`}>
              <p className="text-sm font-medium text-center">
                {!cvReady ? 'Loading OpenCV...' : scanningStatus}
              </p>
              <p className="text-xs text-center mt-1 opacity-80">
                {cvReady ? 'Position the PLX tag within the frame' : 'Please wait while OpenCV initializes'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Embedded mode: render just the camera content (no outer modal shell)
  if (embedded) {
    return cameraContent;
  }

  // Standalone mode: full modal
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`border rounded-xl w-full max-w-2xl shadow-2xl ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
          <h2 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <Camera size={20} />
            Scan PLX Code
          </h2>
          <button onClick={handleClose} className="p-1 hover:bg-slate-800 rounded-lg transition-colors">
            <X size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
          </button>
        </div>

        {cameraContent}

        {/* Cancel button */}
        {!error && (
          <div className="p-4 border-t border-slate-800 flex justify-end">
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
